import React, { createContext, useContext } from 'react';
import { Office, AutoSuggestOption, OfficeBranchDetails } from '../utils/types';
import { mockApi } from '../mockService/mockApi';
import { fetchOfficeApi } from '../api/api';

/**
 * @file AppContext.ts
 * Provides the React context and custom hook for managing global application state
 * in the Office Search frontend. The context includes state and setters for autosuggest options,
 * office results, loading and error states, search query, autosuggest fetch control, detail view toggle,
 * API selection, and office branch details.
 *
 * @module AppContext
 */

type AppContextType = {
  autoSuggestOptions: AutoSuggestOption[];
  setAutoSuggestOptions: React.Dispatch<
    React.SetStateAction<AutoSuggestOption[]>
  >;
  officeResults: Office[];
  setOfficeResults: React.Dispatch<React.SetStateAction<Office[]>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  query: string;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
  shouldFetchAutosuggest: boolean;
  setShouldFetchAutosuggest: React.Dispatch<React.SetStateAction<boolean>>;
  showDetailView: boolean;
  setShowDetailView: React.Dispatch<React.SetStateAction<boolean>>;
  api: typeof fetchOfficeApi | typeof mockApi;
  officeDetails: OfficeBranchDetails;
  setOfficeDetails: React.Dispatch<React.SetStateAction<OfficeBranchDetails>>;
  currentLanguage: string;
};

export const AppContext = createContext<AppContextType | undefined>(undefined);

export const useAppContext = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useAppContext must be used within AppProvider');
  return ctx;
};
